import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  LoaderPinwheel,
  Building2,
  ImageIcon,
  QrCode,
  Save,
} from "lucide-react";

const inputClass =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:outline-none transition-colors bg-white";

const labelClass = "block text-xs font-medium text-gray-500 mb-1";

const SectionHeader = ({ icon: Icon, title, colorClass = "bg-blue-50 text-blue-600" }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
    <div className={`p-1.5 rounded-lg ${colorClass.split(" ")[0]}`}>
      <Icon className={`h-4 w-4 ${colorClass.split(" ")[1]}`} />
    </div>
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
  </div>
);

const LogoPreview = ({ src, alt, label }) => (
  <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
    <div className="w-24 h-24 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex items-center justify-center p-2">
      <img src={src} alt={alt} className="w-full h-full object-contain" />
    </div>
    <p className="text-xs text-gray-400">{label}</p>
  </div>
);

const PerfilEmpresaPage = () => {
  const [empresa, setEmpresa] = useState({
    _id: null,
    nit: "",
    razon_social: "",
    telefono: "",
    logo: null,
    logoEtiqueta: null,
    correo: "",
    direccion: "",
    licencia: "",
    qrGuestAccessEnabled: false,
  });

  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL + "/company";
  const API_AUTH = process.env.REACT_APP_API_URL + "/users/me";

  const fetchEmpresa = async () => {
    try {
      const res = await axios.get(API_AUTH, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const empresaData = res.data?.empresa;
      if (!empresaData) return;
      setEmpresa({
        _id: empresaData._id || null,
        nit: empresaData.nit || "",
        razon_social: empresaData.razon_social || "",
        telefono: empresaData.telefono || "",
        logo: empresaData.logo || "",
        logoEtiqueta: empresaData.logoEtiqueta || "",
        correo: empresaData.correo || "",
        direccion: empresaData.direccion || "",
        licencia: empresaData.licencia || "",
        qrGuestAccessEnabled: Boolean(empresaData.qrGuestAccessEnabled),
      });
    } catch (err) {
      toast.error("Error cargando empresa:", err);
    }
  };

  useEffect(() => {
    fetchEmpresa();
    // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    setLoading(true);
    if (!empresa.nit || !empresa.razon_social || !empresa.correo) {
      toast.error("NIT, Razón Social y Correo son obligatorios");
      setLoading(false);
      return;
    }
    try {
      let res;
      const data = {
        _id: empresa._id,
        nit: empresa.nit,
        razon_social: empresa.razon_social,
        correo: empresa.correo,
        telefono: empresa.telefono || "",
        direccion: empresa.direccion || "",
        licencia: empresa.licencia || "",
        logo: empresa.logo || "",
        logoEtiqueta: empresa.logoEtiqueta || "",
        qrGuestAccessEnabled: Boolean(empresa.qrGuestAccessEnabled),
      };
      if (empresa._id) {
        res = await axios.put(`${API_URL}/${empresa._id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Perfil de empresa actualizado");
      } else {
        // eslint-disable-next-line
        res = await axios.post(API_URL, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Empresa creada correctamente");
      }
      await fetchEmpresa();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "No se pudo guardar la empresa, verifica los campos"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Encabezado de empresa */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
        <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center border-2 border-blue-200 flex-shrink-0">
          {empresa.logo ? (
            <img
              src={empresa.logo}
              alt="Logo empresa"
              className="w-full h-full object-contain rounded-xl p-1"
            />
          ) : (
            <Building2 className="h-7 w-7 text-blue-400" />
          )}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {empresa.razon_social || "Sin razón social"}
          </h2>
          {empresa.nit && (
            <p className="text-sm text-gray-500">NIT: {empresa.nit}</p>
          )}
          {empresa.licencia && (
            <span className="inline-block mt-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full font-medium">
              Licencia: {empresa.licencia}
            </span>
          )}
        </div>
      </div>

      {/* Datos básicos */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={Building2} title="Datos de la Empresa" colorClass="bg-blue-50 text-blue-600" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              NIT <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className={inputClass}
              value={empresa.nit}
              onChange={(e) => setEmpresa({ ...empresa, nit: e.target.value })}
              placeholder="Ej: 900123456-1"
            />
          </div>

          <div>
            <label className={labelClass}>
              Razón Social <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className={inputClass}
              value={empresa.razon_social}
              onChange={(e) =>
                setEmpresa({ ...empresa, razon_social: e.target.value })
              }
              placeholder="Nombre legal de la empresa"
            />
          </div>

          <div>
            <label className={labelClass}>Teléfono</label>
            <input
              type="text"
              className={inputClass}
              value={empresa.telefono}
              onChange={(e) =>
                setEmpresa({ ...empresa, telefono: e.target.value })
              }
              placeholder="Número de contacto"
            />
          </div>

          <div>
            <label className={labelClass}>
              Correo Electrónico <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              className={inputClass}
              value={empresa.correo}
              onChange={(e) =>
                setEmpresa({ ...empresa, correo: e.target.value })
              }
              placeholder="correo@empresa.com"
            />
          </div>

          <div>
            <label className={labelClass}>Dirección</label>
            <input
              type="text"
              className={inputClass}
              value={empresa.direccion}
              onChange={(e) =>
                setEmpresa({ ...empresa, direccion: e.target.value })
              }
              placeholder="Dirección principal"
            />
          </div>

          <div>
            <label className={labelClass}>Licencia</label>
            <input
              type="text"
              className={inputClass}
              value={empresa.licencia}
              onChange={(e) =>
                setEmpresa({ ...empresa, licencia: e.target.value })
              }
              placeholder="Número de licencia"
            />
          </div>
        </div>
      </div>

      {/* Logos */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={ImageIcon} title="Logos" colorClass="bg-purple-50 text-purple-600" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo principal */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {empresa.logo && (
              <LogoPreview src={empresa.logo} alt="Logo empresa" label="Actual" />
            )}
            <div className="flex-1">
              <label className={labelClass}>Logo principal</label>
              <input
                type="file"
                accept="image/*"
                className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-200 rounded-lg cursor-pointer transition-colors"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setEmpresa({ ...empresa, logo: reader.result });
                  };
                  reader.readAsDataURL(file);
                }}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Aparece en reportes y documentos
              </p>
            </div>
          </div>

          {/* Logo etiquetas */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {empresa.logoEtiqueta && (
              <LogoPreview
                src={empresa.logoEtiqueta}
                alt="Logo etiquetas"
                label="Actual"
              />
            )}
            <div className="flex-1">
              <label className={labelClass}>Logo para etiquetas</label>
              <input
                type="file"
                accept="image/*"
                className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 border border-gray-200 rounded-lg cursor-pointer transition-colors"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setEmpresa({ ...empresa, logoEtiqueta: reader.result });
                  };
                  reader.readAsDataURL(file);
                }}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Se imprime en la esquina de cada etiqueta. Recomendado: PNG con
                fondo transparente.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Acceso QR */}
      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-5">
        <SectionHeader icon={QrCode} title="Acceso por QR" colorClass="bg-amber-50 text-amber-600" />
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative mt-0.5">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(empresa.qrGuestAccessEnabled)}
              onChange={(e) =>
                setEmpresa({
                  ...empresa,
                  qrGuestAccessEnabled: e.target.checked,
                })
              }
            />
            <div className="w-10 h-6 bg-gray-200 peer-checked:bg-blue-600 rounded-full transition-colors"></div>
            <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              Permitir lectura pública por QR (invitado sin login)
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Cuando está activo, cualquier persona con el QR podrá ver hoja de
              vida, mantenimientos, componentes, documentos, histórico y
              movimientos en modo solo lectura.
            </p>
          </div>
        </label>
      </div>

      {/* Botón guardar */}
      <div className="flex justify-end pt-1">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
        >
          {loading ? (
            <LoaderPinwheel className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {loading ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
};

export default PerfilEmpresaPage;
